import { z } from 'zod';

const fundCodeBody = z
  .string()
  .min(1, 'Code is required')
  .max(20, 'Code must be at most 20 characters')
  .regex(
    /^[A-Z][A-Z0-9_]*$/,
    'Code must start with a letter and contain only uppercase letters, digits, and underscores',
  );

const fundNameBody = z
  .string()
  .min(1, 'Name is required')
  .max(100, 'Name must be at most 100 characters')
  .transform((s) => s.trim());

const fundDescriptionBody = z
  .string()
  .max(500, 'Description must be at most 500 characters')
  .transform((s) => s.trim())
  .optional();

/**
 * Decimal monetary value as a string. Up to 14 digits total including up to
 * 2 fractional digits. May be 0 (an opening balance of zero is meaningful).
 */
const decimal142NonNegative = z.union([z.string(), z.number()]).transform((v, ctx) => {
  const raw = typeof v === 'number' ? v.toString() : v.trim();
  if (raw.length === 0) {
    ctx.addIssue({ code: 'custom', message: 'Amount is required' });
    return z.NEVER;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Amount must be a non-negative number with at most 2 decimal places',
    });
    return z.NEVER;
  }
  const [whole] = raw.split('.');
  if (whole !== undefined && whole.length > 12) {
    ctx.addIssue({ code: 'custom', message: 'Amount exceeds maximum precision' });
    return z.NEVER;
  }
  return raw;
});

export const createFundRequestSchema = z.object({
  code: fundCodeBody,
  name: fundNameBody,
  description: fundDescriptionBody,
  displayOrder: z.coerce.number().int().min(0).max(9999).default(99),
  openingBalance: decimal142NonNegative.default('0'),
  openingBalanceAsOf: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .optional(),
});
export type CreateFundRequest = z.infer<typeof createFundRequestSchema>;

export const updateFundRequestSchema = z.object({
  name: fundNameBody.optional(),
  description: fundDescriptionBody,
  displayOrder: z.coerce.number().int().min(0).max(9999).optional(),
  openingBalance: decimal142NonNegative.optional(),
  openingBalanceAsOf: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .nullable()
    .optional(),
  isActive: z.boolean().optional(),
});
export type UpdateFundRequest = z.infer<typeof updateFundRequestSchema>;

export interface FundResponse {
  id: string;
  code: string;
  name: string;
  description: string | null;
  openingBalance: string;
  openingBalanceAsOf: string | null;
  openingBalanceLocked: boolean;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}
